"use client";

import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";
import { Badge } from "@/components/ui/badge";

interface OrderSetsListProps {
  orderId: string;
}

const OrderSetsList: React.FC<OrderSetsListProps> = ({ orderId }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const storeType = params?.storeType as string;
  const storeId = params?.storeId as string;

  const { selectedOrder, detailsLoading, error } = useAppSelector(
    (state) => state.order,
  );

  // No need to fetch here - parent component handles it

  const orderSets = selectedOrder?.order_sets || [];

  const handleOrderSetClick = (orderSetId: string) => {
    navigate(
      `/${storeType}/${storeId}/orders/${orderId}/order-sets/${orderSetId}`,
    );
  };

  // Use calculation data from API - no frontend calculations
  const calculation = selectedOrder?.calculation;
  const totalQuantity = calculation?.total_quantity || 0;
  const subtotalAmount = calculation?.grand_total_int || 0;

  const getShippingStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      PICKUP_QUEUED: "Pickup Queued",
      READY_TO_SHIP: "Ready to Ship",
      SHIPPED: "Shipped",
      DELIVERED: "Delivered",
      IN_TRANSIT: "In Transit",
      OUT_FOR_DELIVERY: "Out for Delivery",
    };

    const displayStatus =
      statusMap[status] ||
      status
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase());

    switch (status) {
      case "READY_TO_SHIP":
        return (
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            {displayStatus}
          </Badge>
        );
      case "SHIPPED":
      case "IN_TRANSIT":
        return (
          <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
            {displayStatus}
          </Badge>
        );
      case "DELIVERED":
        return (
          <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
            {displayStatus}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">
            {displayStatus}
          </Badge>
        );
    }
  };

  if (detailsLoading) {
    return (
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <div className="px-4 py-3 bg-muted/50 border-b border-border">
          <h3 className="font-medium text-foreground">ORDER SETS LIST</h3>
        </div>
        <div className="p-8 text-center">
          <div className="text-sm text-muted-foreground">
            Loading order sets...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <div className="px-4 py-3 bg-muted/50 border-b border-border">
          <h3 className="font-medium text-foreground">ORDER SETS LIST</h3>
        </div>
        <div className="p-8 text-center">
          <div className="text-sm text-red-600">
            Error loading order sets: {error}
          </div>
        </div>
      </div>
    );
  }

  if (!orderSets.length) {
    return (
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <div className="px-4 py-3 bg-muted/50 border-b border-border">
          <h3 className="font-medium text-foreground">ORDER SETS LIST</h3>
        </div>
        <div className="p-8 text-center">
          <div className="text-sm text-muted-foreground">
            No order sets found
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-card">
      {/* Header */}
      <div className="px-4 py-3 bg-muted/50 border-b border-border">
        <h3 className="font-medium text-foreground">ORDER SETS LIST</h3>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                ORDER SET
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                PICKUP ADDRESS
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                SHIPPING STATUS
              </th>
              <th className="text-center py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                ITEMS/QTY
              </th>
              <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                AMOUNT
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {orderSets.map((orderSet, index) => {
              const itemCount = orderSet.items?.length || 0;
              const totalQuantity =
                orderSet.items?.reduce((sum, item) => sum + item.quantity, 0) ||
                0;
              const totalAmount =
                orderSet.items?.reduce(
                  (sum, item) => sum + item.price * item.quantity,
                  0,
                ) || 0;

              return (
                <tr
                  key={orderSet.id}
                  className="hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => handleOrderSetClick(orderSet.id)}
                >
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold text-sm">
                          #{index + 1}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">
                          Order Set {index + 1}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {itemCount} unique items
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="space-y-1">
                      {orderSet.address ? (
                        <>
                          <span className="font-medium text-foreground block text-sm">
                            Store Pickup Address
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {orderSet.address?.city && orderSet.address?.state
                              ? `${orderSet.address.city}, ${orderSet.address.state}`
                              : "Address not available"}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          No address set
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    {getShippingStatusBadge(
                      orderSet.shipping_status || "PENDING",
                    )}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="space-y-1">
                      <span className="font-medium text-foreground block">
                        {itemCount} items
                      </span>
                      <span className="text-sm text-muted-foreground">
                        Qty: {totalQuantity}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <span className="font-medium text-foreground">
                      ₹
                      {(totalAmount / 100).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Subtotal */}
      <div className="px-4 py-3 bg-muted/30 border-t border-border">
        <div className="flex justify-between items-center">
          <span className="font-medium text-foreground">Order Sets Total</span>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm text-muted-foreground">
                {orderSets.length} sets
              </div>
              <div className="text-sm text-muted-foreground">
                {totalQuantity} items
              </div>
            </div>
            <span className="font-semibold text-foreground">
              ₹
              {(subtotalAmount / 100).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSetsList;
